import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BlogService } from './blog.service';
import { BlogPost } from './blog.model';
import { NotificationService } from '../../core/notification.service';

@Component({
  standalone: false,
  selector: 'app-blog-list',
  template: `
    <div class="bg-white p-4 rounded shadow">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-semibold">Gerenciar Artigos do Blog</h2>
        <button (click)="createPost()" class="bg-primary text-white px-4 py-2 rounded hover:bg-primary/90">
          + Novo Artigo
        </button>
      </div>

      <div class="mb-4 flex gap-2">
        <select [(ngModel)]="selectedCategory" (change)="onCategoryChange()" class="border rounded px-3 py-2">
          <option value="">Todas as categorias</option>
          <option value="dicas">Dicas</option>
          <option value="inspiracao">Inspiração</option>
          <option value="guias">Guias</option>
        </select>
      </div>

      <div *ngIf="loading" class="text-center py-8 text-muted">Carregando...</div>

      <div *ngIf="!loading && posts.length === 0" class="text-center py-8 text-muted">
        Nenhum artigo encontrado.
      </div>

      <div *ngIf="!loading && posts.length > 0" class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 border-b">
            <tr>
              <th class="text-left p-3 font-medium">Título</th>
              <th class="text-left p-3 font-medium">Categoria</th>
              <th class="text-left p-3 font-medium">Autor</th>
              <th class="text-left p-3 font-medium">Publicado em</th>
              <th class="text-left p-3 font-medium">Visualizações</th>
              <th class="text-left p-3 font-medium">Status</th>
              <th class="text-right p-3 font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let post of posts" class="border-b hover:bg-gray-50">
              <td class="p-3">
                <div class="font-medium">{{ post.title }}</div>
                <div class="text-xs text-muted">/{{ post.slug }}</div>
              </td>
              <td class="p-3">
                <span [class]="getCategoryClass(post.category)" class="px-2 py-1 rounded text-xs font-medium">
                  {{ getCategoryLabel(post.category) }}
                </span>
              </td>
              <td class="p-3 text-muted">{{ post.author || '—' }}</td>
              <td class="p-3 text-muted">{{ post.publishedAt | date:'dd/MM/yyyy HH:mm' }}</td>
              <td class="p-3 text-muted">{{ post.views }}</td>
              <td class="p-3">
                <span [class]="!!post.isPublished ? 'text-green-600 bg-green-50' : 'text-gray-600 bg-gray-50'" class="px-2 py-1 rounded text-xs font-medium">
                  {{ !!post.isPublished ? 'Publicado' : 'Rascunho' }}
                </span>
              </td>
              <td class="p-3 text-right">
                <button (click)="editPost(post)" class="text-xs px-2 py-1 border rounded hover:bg-gray-50 mr-2" title="Editar">
                  ✏️ Editar
                </button>
                <button (click)="deletePost(post)" class="text-xs px-2 py-1 text-red-600 border border-red-300 rounded hover:bg-red-50" title="Deletar">
                  🗑️ Deletar
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div *ngIf="totalPages > 1" class="mt-4 flex items-center justify-between">
        <div class="text-sm text-muted">
          Página {{ page }} de {{ totalPages }} • Total: {{ total }} artigos
        </div>
        <div class="flex gap-2">
          <button (click)="onPageChange(page - 1)" [disabled]="page === 1" class="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
            Anterior
          </button>
          <button (click)="onPageChange(page + 1)" [disabled]="page >= totalPages" class="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
            Próxima
          </button>
        </div>
      </div>
    </div>
  `
})
export class BlogListComponent implements OnInit {
  posts: BlogPost[] = [];
  loading = false;
  page = 1;
  pageSize = 20;
  total = 0;
  totalPages = 1;
  selectedCategory = '';

  constructor(
    private svc: BlogService,
    private router: Router,
    private notification: NotificationService
  ) {}

  ngOnInit() {
    this.loadPosts();
  }

  loadPosts() {
    this.loading = true;
    console.log('[BlogList] Carregando posts - page:', this.page, 'pageSize:', this.pageSize, 'category:', this.selectedCategory);
    this.svc.getPosts(this.page, this.pageSize, this.selectedCategory || undefined).subscribe({
      next: (res: any) => {
        console.log('[BlogList] Resposta da API:', res);
        this.posts = res?.data || [];
        this.total = res?.meta?.total || 0;
        this.totalPages = res?.meta?.totalPages || 1;
        this.loading = false;
        console.log('[BlogList] Posts carregados:', this.posts.length, 'Total:', this.total);
        // Debug do isPublished
        if (this.posts.length > 0) {
          console.log('[BlogList] Primeiro post - isPublished:', this.posts[0].isPublished, 'tipo:', typeof this.posts[0].isPublished);
        }
      },
      error: (err) => {
        console.error('[BlogList] erro ao carregar', err);
        this.notification.showError('Erro ao carregar artigos');
        this.loading = false;
      }
    });
  }

  onCategoryChange() {
    this.page = 1;
    this.loadPosts();
  }

  onPageChange(newPage: number) {
    this.page = newPage;
    this.loadPosts();
  }

  createPost() {
    this.router.navigate(['/admin/blog/new']);
  }

  editPost(post: BlogPost) {
    this.router.navigate(['/admin/blog', post.id, 'edit'], { 
      state: { post } 
    });
  }

  deletePost(post: BlogPost) {
    if (!confirm(`Deseja realmente deletar o artigo "${post.title}"?`)) {
      return;
    }

    this.svc.deletePost(post.id).subscribe({
      next: () => {
        this.notification.showSuccess('Artigo deletado com sucesso');
        this.loadPosts();
      },
      error: (err) => {
        console.error('[BlogList] erro ao deletar', err);
        this.notification.showError('Erro ao deletar artigo');
      }
    });
  }

  getCategoryClass(category: string): string {
    const classes: any = {
      'dicas': 'bg-blue-100 text-blue-700',
      'inspiracao': 'bg-purple-100 text-purple-700',
      'guias': 'bg-green-100 text-green-700'
    };
    return classes[category] || 'bg-gray-100 text-gray-700';
  }

  getCategoryLabel(category: string): string {
    const labels: any = {
      'dicas': 'Dicas',
      'inspiracao': 'Inspiração',
      'guias': 'Guias'
    };
    return labels[category] || category;
  }
}
